// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import type { LedgerState, Account } from '../../types.js';
import { ASSET_TYPES } from '../../lib/eventLog.js';

// Smart defaults for the mobile quick-entry screen. All derived from the existing
// event-sourced ledger — no new state, no schema change.

export interface PayeeSuggestion {
  name: string;
  // Most likely counter-account for this payee (the category you usually file it
  // under), so selecting a recent payee also pre-fills the "To" account.
  toAccountId: string | null;
  count: number;
}

// Non-asset side of a spend: for a cash purchase the "to" account is the expense
// (positive amount) leg. We treat any non-asset split as the category.
function isAssetAccount(account: Account | undefined): boolean {
  return account ? ASSET_TYPES.has(account.type) : false;
}

// Recent, frequently-used payees with their usual category, most useful first.
// Ranking blends frequency and recency: recent repeat payees float to the top,
// which is what you want for "tap the chip, done".
export function getRecentPayees(ledger: LedgerState, limit = 6): PayeeSuggestion[] {
  const txs = [...ledger.transactions.values()]
    .filter((t) => !t.voided && t.description.trim() !== '')
    .sort((a, b) => (a.date < b.date ? 1 : -1)); // most recent first

  interface Acc {
    name: string;
    count: number;
    firstIndex: number; // position of most-recent occurrence (lower = more recent)
    toCounts: Map<string, number>;
  }
  const byName = new Map<string, Acc>();

  txs.forEach((tx, index) => {
    const key = tx.description.trim();
    const lower = key.toLowerCase();
    let entry = byName.get(lower);
    if (!entry) {
      entry = { name: key, count: 0, firstIndex: index, toCounts: new Map() };
      byName.set(lower, entry);
    }
    entry.count += 1;
    // The counter-account = the non-asset leg (usually the expense category).
    const category = tx.splits.find((s) => !isAssetAccount(ledger.accounts.get(s.accountId)));
    if (category) {
      entry.toCounts.set(category.accountId, (entry.toCounts.get(category.accountId) ?? 0) + 1);
    }
  });

  const suggestions = [...byName.values()].map((e) => {
    let toAccountId: string | null = null;
    let best = 0;
    for (const [accId, c] of e.toCounts) {
      if (c > best) {
        best = c;
        toAccountId = accId;
      }
    }
    return { name: e.name, toAccountId, count: e.count, firstIndex: e.firstIndex };
  });

  // Recency-first, then frequency as a tiebreaker.
  suggestions.sort((a, b) => a.firstIndex - b.firstIndex || b.count - a.count);
  return suggestions.slice(0, limit).map(({ name, toAccountId, count }) => ({ name, toAccountId, count }));
}

// Autocomplete matches for a typed payee fragment.
export function matchPayees(ledger: LedgerState, fragment: string, limit = 5): PayeeSuggestion[] {
  const q = fragment.trim().toLowerCase();
  if (!q) return [];
  const all = getRecentPayees(ledger, 200);
  return all.filter((p) => p.name.toLowerCase().includes(q)).slice(0, limit);
}

// The default "From" account for quick entry: the user's configured account, else
// the first CASH account, else the first asset account. Placeholder/hidden
// accounts are never eligible.
export function resolveDefaultFromAccount(
  ledger: LedgerState,
  configuredId: string | null,
): Account | null {
  const usable = (a: Account | undefined): a is Account =>
    !!a && !a.placeholder && !a.hidden && a.type !== 'ROOT';

  if (configuredId) {
    const configured = ledger.accounts.get(configuredId);
    if (usable(configured)) return configured;
  }
  const accounts = [...ledger.accounts.values()].filter(usable);
  const cash = accounts.find((a) => a.type === 'CASH');
  if (cash) return cash;
  const bank = accounts.find((a) => a.type === 'BANK');
  if (bank) return bank;
  return accounts.find((a) => ASSET_TYPES.has(a.type)) ?? null;
}

// The last expense account used, as a fallback "To" when a payee has no history.
export function lastUsedExpenseAccount(ledger: LedgerState): string | null {
  const txs = [...ledger.transactions.values()]
    .filter((t) => !t.voided)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
  for (const tx of txs) {
    const expense = tx.splits.find((s) => ledger.accounts.get(s.accountId)?.type === 'EXPENSE');
    if (expense) return expense.accountId;
  }
  return null;
}
